// @ts-strict-ignore
// eslint-disable-next-line no-restricted-imports
import produce, {Draft} from 'immer';
import * as React from 'react';

import {DisplayBookmark} from 'common/report/dashboards/types';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {InsightsBookmarkParams} from 'common/types/reports/bookmark';
import {CurrentProjectResource} from 'common/report/model-resources/projects';
import {ProjectFeatureFlag} from 'common/report/constants';
import {LocalizationStore} from 'common/i18n/store';

import {hasValidBookmarkParams} from './validation';
import {getSerializedClause, syncMetricsAcrossBookmark} from './hydrate-bookmark';

type ParamsMutator = (s: Draft<InsightsBookmarkParams>) => void;

type Bookmark = DisplayBookmark<InsightsBookmarkParams>;
type ReportState = Bookmark & {
  /** provides an immutable setter on bookmark params  */
  updateParams: (fn: ParamsMutator) => void;
  isValid: () => boolean;
};

/** Options passed on every UI interaction that leads to a bookmark param update */
type ApplyParamsDetail = {queryReason?: QueryReason; requery?: boolean; hasChangedChartType?: boolean};

/**
 * Serializes a given bookmark by transforming its `sections.show` clauses
 * into their serialized form using canonical metrics.
 *
 * This also strips away all UI state from the bookmark
 */
export function serializeBookmark(bookmark: DisplayBookmark | null | undefined) {
  if (!bookmark || `sections` in bookmark.params === false) {
    return bookmark;
  }

  return produce(bookmark, (draft) => {
    draft.params.sections.show = draft.params.sections.show.map((clause) => getSerializedClause(clause));
  });
}

export type ApplyParamsFunction = (params: InsightsBookmarkParams, options?: ApplyParamsDetail) => void;

/**
 * Provides a Panel context that hosts a @type{Bookmark} instance and utilities to apply immutable modifiers
 *
 * This context also provides an instance of @type{ReportState} which is propagated as a React context value
 * inside @see{useReportBookmark}. This is done to avoid propagating Panel context internals to React components
 * in React hooks.
 */
export class ReportBookmarkManager {
  private declare _onApplyParams: ApplyParamsFunction;
  private _bookmark: Bookmark | null = null;

  private metrics: MetricsResource = null;
  private behaviors: BehaviorsResource = null;
  private currentProject: CurrentProjectResource = null;
  private i18n: LocalizationStore = null;

  get exposedValue(): ReportState {
    return {
      ...this._bookmark,

      updateParams: (fn, options: ApplyParamsDetail = {}) => {
        const updatedBookmark = produce(this._bookmark, (draft) => {
          fn(draft.params);
        });

        this._onApplyParams(updatedBookmark.params, options);
      },

      isValid(): boolean {
        return hasValidBookmarkParams(this.params);
      },
    };
  }

  updateBookmark(bookmark: Bookmark) {
    const hasInsightsBookmark = `sections` in (this._bookmark?.params ?? {});
    this._bookmark = produce(bookmark, (draft) => {
      /**
       * makes sure any edits to saved entities are reflected in the bookmark
       * this isn't necessary when loading a bookmark since the backend
       * will return a fully hydrated bookmark
       */
      if (`sections` in draft.params) {
        draft.params = syncMetricsAcrossBookmark({
          params: draft.params,
          previousParams: hasInsightsBookmark ? this.bookmark.params : null,
          metrics: this.metrics,
          behaviors: this.behaviors,
          i18n: this.i18n,
          isFirstClassMetricsEnabled: this.currentProject
            .get()
            .value?.hasFeatureFlag(ProjectFeatureFlag.FirstClassMetrics),
        });
      }
    });
  }

  public syncBookmark(bookmark: Bookmark) {
    this.updateBookmark(bookmark);
    return this.bookmark;
  }

  get bookmark() {
    return this._bookmark;
  }

  public setup(
    onApplyParams: ApplyParamsFunction,
    {
      metrics,
      behaviors,
      i18n,
      currentProject,
    }: {
      metrics: MetricsResource;
      behaviors: BehaviorsResource;
      currentProject: CurrentProjectResource;
      i18n: LocalizationStore;
    },
  ) {
    this._onApplyParams = onApplyParams;
    this.metrics = metrics;
    this.behaviors = behaviors;
    this.i18n = i18n;
    this.currentProject = currentProject;
  }

  public teardown() {
    this._onApplyParams = null;
    this._bookmark = null;
  }
}

const ReportBookmarkManagerContext = React.createContext<ReportState>(
  // @ts-ignore
  {
    params: null,
  },
);

export const ReportBookmarkManagerConsumer = ReportBookmarkManagerContext.Consumer;

/**
 * Context provider for the ReportBookmark Panel context.
 *
 * This prevents leaking Panel context details for React components
 */
export function ReportBookmarkManagerProvider(props: {
  children: React.ReactNode;
  value: ReportBookmarkManager | ReportState;
}) {
  const contextValue = props.value instanceof ReportBookmarkManager ? props.value.exposedValue : props.value;
  return (
    <ReportBookmarkManagerContext.Provider value={new Proxy(contextValue, {})}>
      {props.children}
    </ReportBookmarkManagerContext.Provider>
  );
}

export const useReportBookmark = (): ReportState => {
  const contextValue = React.useContext(ReportBookmarkManagerContext);
  if (contextValue instanceof ReportBookmarkManager) {
    throw new Error(`Attempting to use a Panel context inside a React component`);
  }

  return contextValue;
};
